import { Box, Card, Skeleton, Stack, Typography } from '@mui/material'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import Download from 'components/core/download'
import { C1DrawerInner } from 'components/core/drawer/C1DrawerInner'
import { EmptyList } from 'components/core/empty-list/index'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { getShortString } from 'components/core/time/display/index'
import { QUERIES, queryToLabel } from 'components/pages/explore/query/Query'
import { Download as DownloadIcon } from 'lucide-react'
import { ExportDataActionState, ListExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { Timestamp as pbtsTimestamp } from 'pbts/google/protobuf/timestamp'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { exportDataActionsListApi } from 'redux_toolkit/export-data'

type QueryMetadata = {
    text: string
}

const pageSize = 10
const queryIdToMetadata = (intl: IntlShape, queryId: QueryId): QueryMetadata => {
    switch (queryId) {
        case QueryId.ACCESS_PROFILE_MEMBERSHIP:
            return {
                text: queryToLabel(intl, QUERIES.AccessProfileMembership),
            }
        case QueryId.ALL_APP_USERS:
            return {
                text: queryToLabel(intl, QUERIES.AllAppUsers),
            }
        case QueryId.RESOURCES:
            return {
                text: queryToLabel(intl, QUERIES.Resources),
            }
        case QueryId.APP_USERS_WITHOUT_OWNER:
            return {
                text: queryToLabel(intl, QUERIES.AppUsersWithoutOwner),
            }
        case QueryId.APP_ENTITLEMENTS_WITH_DEACTIVATED_OWNER:
            return {
                text: queryToLabel(intl, QUERIES.AppEntitlementsWithDeactivatedOwner),
            }
        case QueryId.HIGH_RISK_ACCOUNTS:
            return {
                text: queryToLabel(intl, QUERIES.HighRiskAccounts),
            }
        case QueryId.HIGH_RISK_ROLE_GRANTS_TEMPORARY:
            return {
                text: queryToLabel(intl, QUERIES.HighRiskRoleGrantsTemporary),
            }
        case QueryId.HIGH_RISK_ROLE_GRANTS_PERMANENT:
            return {
                text: queryToLabel(intl, QUERIES.HighRiskRoleGrantsPermanent),
            }
        case QueryId.STANDING_PRIVILEGES:
            return {
                text: queryToLabel(intl, QUERIES.StandingPrivileges),
            }
        case QueryId.APPS_WITH_DEACTIVATED_OWNERS:
            return {
                text: queryToLabel(intl, QUERIES.AppsWithDeactivatedOwners),
            }
        case QueryId.ORPHANED_ACCOUNTS:
            return {
                text: queryToLabel(intl, QUERIES.OrphanedAccounts),
            }
        case QueryId.APP_RESOURCES_WITH_DEACTIVATED_OWNER:
            return {
                text: queryToLabel(intl, QUERIES.AppResourcesWithDeactivatedOwner),
            }
        case QueryId.APP_ENTITLEMENTS_WITH_ONE_OWNER:
            return {
                text: queryToLabel(intl, QUERIES.AppEntitlementsWithOneOwner),
            }
        case QueryId.APPS_WITH_ONE_OWNER:
            return {
                text: queryToLabel(intl, QUERIES.AppsWithOneOwner),
            }
        case QueryId.APP_USERS_NOT_LOGGED_IN_RECENTLY:
            return {
                text: queryToLabel(intl, QUERIES.AppUsersNotLoggedInRecently),
            }
        case QueryId.USERS_WITHOUT_MANAGER:
            return {
                text: queryToLabel(intl, QUERIES.UsersWithoutManager),
            }
        case QueryId.PER_APP_ACCOUNTS:
            return {
                text: intl.formatMessage({ defaultMessage: 'App accounts', id: 'per_app_accounts' }),
            }
        case QueryId.VALIDATE_SCOPE:
            return {
                text: intl.formatMessage({ defaultMessage: 'Validate scope', id: 'validate_scope' }),
            }
        case QueryId.ACCESS_VIOLATIONS:
            return {
                text: intl.formatMessage({ defaultMessage: 'Access violations', id: 'access_violations' }),
            }
        case QueryId.PAST_GRANTS:
            return {
                text: intl.formatMessage({ defaultMessage: 'Past grants', id: 'past_grants' }),
            }
        case QueryId.USERS_WITH_UNSPECIFIED_EMPLOYMENT_STATUS:
            return {
                text: intl.formatMessage({ defaultMessage: 'Users with unspecified employment status', id: 'task_search' }),
            }
        case QueryId.TASK_SEARCH:
            return {
                text: intl.formatMessage({ defaultMessage: 'Tasks', id: 'task_search' }),
            }
        case QueryId.ENTITLEMENT_GRANTS:
            return {
                text: intl.formatMessage({ defaultMessage: 'Entitlement grants', id: 'entitlement_grants' }),
            }
        case QueryId.USER_SEARCH:
            return {
                text: intl.formatMessage({ defaultMessage: 'Users', id: 'user_search' }),
            }
        case QueryId.GRANT_FEED:
            return {
                text: intl.formatMessage({ defaultMessage: 'Grant feed', id: 'grant_feed' }),
            }
        case QueryId.SHADOW_APPS:
            return {
                text: intl.formatMessage({ defaultMessage: 'Shadow apps', id: 'shadow_apps' }),
            }
        case QueryId.APP_USER_GRANTS:
            return {
                text: intl.formatMessage({ defaultMessage: 'App user grants', id: 'app_user_grants' }),
            }
        case QueryId.ACTIVE_EXTERNAL_ACCOUNTS:
            return {
                text: intl.formatMessage({ defaultMessage: 'Active external accounts', id: 'active_external_accounts' }),
            }
        case QueryId.CAMPAIGN_REVIEWER_DECISIONS:
            return {
                text: intl.formatMessage({ defaultMessage: 'Campaign reviewer decisions', id: 'reviewer_decisions' }),
            }
        case QueryId.CAMPAIGN_OPEN_REVIEWS_BY_USER:
            return {
                text: intl.formatMessage({ defaultMessage: 'Campaign open reviews by user', id: 'campaign_open_reviews_by_user' }),
            }
        case QueryId.CAMPAIGN_PROGRESS_BY_APP:
            return {
                text: intl.formatMessage({ defaultMessage: 'Campaign progress by app', id: 'campaign_progress_by_app' }),
            }
        case QueryId.OPEN_EXTENSION_TICKETS:
            return {
                text: intl.formatMessage({ defaultMessage: 'Open extension requests', id: 'open_extension_tickets' }),
            }
        case QueryId.EXPIRING_GRANTS:
            return {
                text: intl.formatMessage({ defaultMessage: 'Expiring grants', id: 'expiring_grants' }),
            }
        case QueryId.TSDB_TOP_VALUES:
            return {
                text: intl.formatMessage({ defaultMessage: 'App top values', id: 'tsdb_top_values' }),
            }
        case QueryId.APP_ENTITLEMENTS:
            return {
                text: intl.formatMessage({ defaultMessage: 'App entitlements', id: 'app_entitlements' }),
            }
        case QueryId.APP_RESOURCES:
            return {
                text: intl.formatMessage({ defaultMessage: 'App resources', id: 'app_resources' }),
            }
        default:
            return {
                text: intl.formatMessage({ defaultMessage: 'Unknown', id: 'unknown' }),
            }
    }
}

const DownloadSkeleton = (): ReactElement => <Stack spacing={3} direction="column" sx={{ minWidth: '400px' }}>
    {
        [...Array(5)].map((_, i) => <Card key={i} variant="outlined">
            <Stack spacing={3} direction="row" justifyContent="space-between" alignItems="center" p={3}>
                <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                </Stack>
                <Skeleton variant="text" sx={{ width: '100px' }} />
            </Stack>
        </Card>)
    }
</Stack>

const DownloadItems = (): ReactElement => {
    const intl = useIntl()
    const {
        data,
        isError,
        isLoading,
        error,
        shouldInvalidateList,
    } = exportDataActionsListApi.useList(ListExportDataRequest.create({}), {
        pageSize,
        notify: true,
        autoReload: true,
    })
    const invalidateList = exportDataActionsListApi.useInvalidateList()

    useEffect(() => {
        if (shouldInvalidateList && !isError) {
            invalidateList()
        }
    }, [invalidateList, isError, shouldInvalidateList])

    if (isLoading) {
        return <DownloadSkeleton />
    }

    if (isError && error) {
        return <APIError err={error} />
    }

    return <Box sx={{ minWidth: '400px' }}>
        {
            data && data.flat().length === 0
                ? <EmptyList
                    icon={<C1Icon icon={DownloadIcon} />}
                    text={<FormattedMessage defaultMessage="No downloads available" id="downloads_none" />}
                />
                : <Stack spacing={3} direction="column" sx={{ minWidth: '400px' }}>
                    {data.flat().map((item) => <Card variant="outlined" key={item.id}>
                        <Stack spacing={3} direction="row" justifyContent="space-between" alignItems="center" p={3}>
                            <Stack direction="column" spacing={1}>
                                <Typography variant="h6" component="div">
                                    {item.displayName ? item.displayName : queryIdToMetadata(intl, item.queryId).text}
                                </Typography>
                                {
                                    item.createdAt && <Typography component="div">
                                        {getShortString({ d: pbtsTimestamp.toDate(item.createdAt), variant: 'date' })}
                                    </Typography>
                                }
                            </Stack>
                            {
                                item.state === ExportDataActionState.READY && item.downloadUrl && <Download downloadable={item} />
                            }
                            {
                                item.state === ExportDataActionState.PENDING && <C1CircularProgress />
                            }
                            {
                                item.state === ExportDataActionState.ERROR && <Typography color="error">
                                    <FormattedMessage defaultMessage="Failed" id="downloads_failed" />
                                </Typography>
                            }
                        </Stack>
                    </Card>)}
                </Stack>
        }
    </Box>
}

export const Downloads = (): ReactElement => {
    return <C1DrawerInner>
        <C1DrawerInner.Header>
            <FormattedMessage defaultMessage="Downloads" id="downloads" />
        </C1DrawerInner.Header>
        <C1DrawerInner.Content>
            <DownloadItems />
        </C1DrawerInner.Content>
    </C1DrawerInner>
}
